import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  structuredData?: Array<Record<string, unknown>>;
}

export function SEOHead({ title, description, canonical, ogImage = '/logo.png', structuredData }: SEOHeadProps) {
  const fullCanonical = canonical ? `https://www.lykketerapien.dk${canonical}` : undefined;

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    type Cleanup = () => void;
    const cleanups: Cleanup[] = [];

    const previousTitle = document.title;
    document.title = title;
    cleanups.push(() => {
      document.title = previousTitle;
    });

    const updateMetaTag = (attribute: 'name' | 'property', value: string, content: string): Cleanup => {
      const selector = `meta[${attribute}="${value}"]`;
      const existing = document.head.querySelector<HTMLMetaElement>(selector);

      if (existing) {
        const previousContent = existing.getAttribute('content');
        existing.setAttribute('content', content);
        return () => {
          if (previousContent !== null) {
            existing.setAttribute('content', previousContent);
          } else {
            existing.removeAttribute('content');
          }
        };
      }

      const meta = document.createElement('meta');
      meta.setAttribute(attribute, value);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);

      return () => {
        document.head.removeChild(meta);
      };
    };

    const updateLinkTag = (rel: string, href: string): Cleanup => {
      const selector = `link[rel="${rel}"]`;
      const existing = document.head.querySelector<HTMLLinkElement>(selector);

      if (existing) {
        const previousHref = existing.getAttribute('href');
        existing.setAttribute('href', href);
        return () => {
          if (previousHref !== null) {
            existing.setAttribute('href', previousHref);
          } else {
            existing.removeAttribute('href');
          }
        };
      }

      const link = document.createElement('link');
      link.setAttribute('rel', rel);
      link.setAttribute('href', href);
      document.head.appendChild(link);

      return () => {
        document.head.removeChild(link);
      };
    };

    cleanups.push(updateMetaTag('name', 'description', description));
    cleanups.push(updateMetaTag('property', 'og:title', title));
    cleanups.push(updateMetaTag('property', 'og:description', description));
    cleanups.push(updateMetaTag('property', 'og:image', ogImage));

    if (fullCanonical) {
      cleanups.push(updateLinkTag('canonical', fullCanonical));
      cleanups.push(updateMetaTag('property', 'og:url', fullCanonical));
    }

    if (structuredData?.length) {
      structuredData.forEach((data) => {
        const script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);

        cleanups.push(() => {
          document.head.removeChild(script);
        });
      });
    }

    return () => {
      cleanups.reverse().forEach((cleanup) => {
        try {
          cleanup();
        } catch (error) {
          console.error('Error cleaning up SEO tags:', error);
        }
      });
    };
  }, [title, description, fullCanonical, ogImage, structuredData]);

  return null;
}
